import React, { useRef, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { identifyCustomerService } from "services/api-service";
import { toast } from "react-toastify";

const SmartCounter = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const history = useHistory();
  
  const [isScanning, setIsScanning] = useState(true);
  const [cameraError, setCameraError] = useState(false);

  // Initialize the native HTML5 camera
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "user" } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraError(false);
    } catch (err) {
      console.error("Camera access denied or unavailable", err);
      setCameraError(true);
      setIsScanning(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
  };

  // Scanning Loop
  useEffect(() => {
    let interval;
    if (isScanning && !cameraError) {
      // Capture and send a frame to the backend every 2.5 seconds
      interval = setInterval(() => {
        captureAndIdentify();
      }, 2500); 
    }
    return () => clearInterval(interval);
  }, [isScanning, cameraError]);

  const captureAndIdentify = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Ensure the video is actually playing before capturing
    if (video.readyState !== 4) return; 

    // Match canvas size to video stream
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageSrc = canvas.toDataURL("image/jpeg");
    if (!imageSrc || imageSrc === "data:,") return; 

    try {
      const payload = { images: [imageSrc] };
      const response = await identifyCustomerService(payload);

      // Check the custom status flag sent by our updated Python backend
      if (response.data && response.data.status === "success") {
        setIsScanning(false); // Immediately stop scanning
        const matchedId = response.data.student_id;
        
        toast.success(`Customer Recognized! Opening Profile...`);
        
        // Redirect to the AddStudentDetailsE component via the /bra route
        history.push("/admin/bra", { student_id: matchedId });
      } else {
        // Silently log 'no_face' or 'not_found' without throwing red console errors
        console.log(`[Scanner] Status: ${response.data.status}`);
      }
    } catch (error) {
      console.log("[Scanner] Network pending or unavailable...");
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '30px', paddingBottom: '60px' }}>
      <h2 style={{ fontWeight: 'bold', color: '#2B3674' }}>Smart Auto-Counter</h2>
      <p style={{ color: '#A3AED0', marginBottom: '30px' }}>
        Point the camera at the customer to automatically pull up their profile and pricing details.
      </p>
      
      <div style={{ 
        border: '6px solid #4318FF', 
        borderRadius: '16px', 
        overflow: 'hidden', 
        width: '640px', 
        height: '480px',
        boxShadow: '0 10px 30px rgba(67, 24, 255, 0.2)',
        position: 'relative',
        backgroundColor: '#1E293B',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        
        {cameraError ? (
          <div style={{ color: '#94A3B8', textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>📷</div>
            <h3 style={{ margin: '0 0 10px 0', color: '#F8FAFC' }}>Camera Not Found</h3>
            <p style={{ margin: 0 }}>Please connect a webcam or allow browser camera permissions.</p>
            <button 
              onClick={startCamera}
              style={{ marginTop: '20px', padding: '8px 16px', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
            >
              Retry Connection
            </button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} 
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </>
        )}

        {isScanning && !cameraError && (
          <div style={{
            position: 'absolute', top: 20, right: 20, 
            backgroundColor: 'rgba(0,0,0,0.6)', color: '#05CD99', 
            padding: '8px 16px', borderRadius: '20px', 
            fontWeight: 'bold', fontSize: '14px',
            display: 'flex', alignItems: 'center', gap: '8px',
            animation: 'pulse 1.5s infinite'
          }}>
            <div style={{ width: 10, height: 10, backgroundColor: '#05CD99', borderRadius: '50%' }}></div>
            Scanning...
          </div>
        )}
      </div>
      
      {!cameraError && (
        <button 
          onClick={() => setIsScanning(!isScanning)}
          style={{ 
            marginTop: '30px', padding: '12px 30px', 
            backgroundColor: isScanning ? '#EE5D50' : '#4318FF', 
            color: 'white', border: 'none', borderRadius: '12px', 
            cursor: 'pointer', fontWeight: 'bold', fontSize: '16px',
            boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
          }}
        >
          {isScanning ? "Pause Scanner" : "Resume Scanner"}
        </button>
      )}

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default SmartCounter;
